import { createPortal } from "react-dom";
import type { ReactNode, RefObject } from "react";
import { useAnchoredPosition } from "./useAnchoredPosition";

/**
 * Popover rendered through a portal on `document.body` with `position:
 * fixed` computed from the anchor's rect (see `anchoredPosition.ts`). Escaping
 * the DOM tree is what keeps listboxes from being clipped by a modal body's
 * `overflow-y: auto`; width follows the anchor so the list always lines up
 * with its trigger. Mousedown inside is default-prevented so the trigger keeps
 * focus (the combobox pattern drives options via `aria-activedescendant`).
 */
export function FloatingPanel({
  anchorRef,
  open,
  panelRef,
  className,
  children,
}: {
  anchorRef: RefObject<HTMLElement | null>;
  open: boolean;
  panelRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  children: ReactNode;
}) {
  const position = useAnchoredPosition(anchorRef, open);
  if (!open || !position) return null;

  return createPortal(
    <div
      ref={panelRef}
      className={className ? `v3-combobox__popover ${className}` : "v3-combobox__popover"}
      data-placement={position.placement}
      style={{
        position: "fixed",
        left: position.left,
        width: position.width,
        top: position.top,
        bottom: position.bottom,
        maxHeight: position.maxHeight,
      }}
      onMouseDown={(event) => event.preventDefault()}
    >
      {children}
    </div>,
    document.body,
  );
}
